// TranslationModal.tsx
import React, { useState, useRef } from "react"
import { Modal } from "antd"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLanguage } from "@fortawesome/free-solid-svg-icons"
import TranslationModalContent from "./TranslationModalContent.component"
import { useQuestionnaire } from "features/QuestionnaireEditor/api/useQuestionnaire"
import { useDraggablePanel } from "hooks/core/useDraggablePanel"
import { Project } from "stores/projectStore"
import { Variant } from "types/questionnaire.types"

/**
 * Props interface for the TranslationModal component
 */
interface TranslationModalProps {
  /** Function to save the questionnaire */
  saveQuestionnaire: (updatedVariants?: Variant[]) => Promise<void>
  /** The project object */
  project: Project
  /** Whether the button should be disabled */
  isDisabled?: boolean
}

/**
 * TranslationModal component for managing questionnaire translations
 *
 * @component
 * @param props - The component props
 * @returns The rendered TranslationModal component
 */
const TranslationModal: React.FC<TranslationModalProps> = ({
  saveQuestionnaire,
  project,
  isDisabled = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false)
  const [parseFailed, setParseFailed] = useState<boolean>(false)

  const { checkQuestionnaireByID } = useQuestionnaire()

  const panelRef = useRef<HTMLDivElement>(null)
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel(
    { x: 0, y: 0 },
    { panelRef, bounds: true }
  )

  // When dragged, pin the modal at absolute viewport coords (antd centers it otherwise)
  const dragStyle: React.CSSProperties =
    position.x !== 0 || position.y !== 0
      ? { position: "fixed", left: position.x, top: position.y, margin: 0 }
      : {}

  /**
   * Handles opening the modal and checking the questionnaire
   *
   * @async
   * @function handleOpenModal
   */
  const handleOpenModal = async (): Promise<void> => {
    const res = await checkQuestionnaireByID(project.id)
    if (
      res.data.result.notifications &&
      Object.keys(res.data.result.notifications).length > 0
    ) {
      setParseFailed(true)
      setIsModalVisible(true)
    } else {
      setParseFailed(false)
      setIsModalVisible(true)
    }
  }

  return (
    <>
      <DataQButton onClick={handleOpenModal} type="secondary" isDisabled={isDisabled}>
        Translations
        <FontAwesomeIcon icon={faLanguage} className="ml-2" />
      </DataQButton>
      <Modal
        title={
          <div
            className="modal-header w-full cursor-move flex items-center"
            onMouseDown={handleHeaderMouseDown}
          >
            Translation Manager
            <FontAwesomeIcon
              icon={faLanguage}
              className="mx-2 text-[#36c3ed]"
            />
          </div>
        }
        open={isModalVisible}
        destroyOnHidden={true}
        mask={false}
        maskClosable={false}
        footer={null}
        width={750}
        wrapClassName="pointer-events-none"
        onCancel={() => setIsModalVisible(false)}
        modalRender={(modal: React.ReactNode) => (
          <div
            ref={panelRef}
            className="shadow-lg rounded-lg overflow-hidden"
            style={{
              width: 750,
              cursor: isDragging ? "grabbing" : undefined,
              ...dragStyle,
            }}
          >
            {modal}
          </div>
        )}
      >
        <TranslationModalContent
          saveQuestionnaire={saveQuestionnaire}
          project={project}
          parseFailed={parseFailed}
          setIsModalVisible={setIsModalVisible}
        />
      </Modal>
    </>
  )
}

export default TranslationModal
